import { ArrowLeft, FileText, Shield, AlertTriangle } from "lucide-react"
import { Link } from "react-router"

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/login"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Terms of Service</h1>
            <p className="text-sm text-gray-500">Last updated: January 27, 2026</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          {/* Quick Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 rounded-lg p-4">
              <FileText className="h-6 w-6 text-blue-600 mb-2" />
              <h3 className="font-medium text-gray-900">Clear Terms</h3>
              <p className="text-sm text-gray-600">Plain language, no legal jargon</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <Shield className="h-6 w-6 text-green-600 mb-2" />
              <h3 className="font-medium text-gray-900">Your Content</h3>
              <p className="text-sm text-gray-600">You own what you create</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <AlertTriangle className="h-6 w-6 text-orange-600 mb-2" />
              <h3 className="font-medium text-gray-900">Fair Use</h3>
              <p className="text-sm text-gray-600">Simple, reasonable guidelines</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="prose prose-gray max-w-none">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using Audra Flow (the "Service"), you agree to be bound by these
              Terms of Service. If you do not agree to these Terms, you may not access or use the Service.
            </p>

            <h2>2. Description of Service</h2>
            <p>Audra Flow is a product management and design platform that provides:</p>
            <ul>
              <li>Project and artifact management</li>
              <li>AI-powered document analysis and insights</li>
              <li>Collaboration tools for product teams</li>
              <li>Knowledge base and recommendation features</li>
            </ul>

            <h2>3. User Accounts</h2>
            <h3>Account Creation</h3>
            <p>
              You must provide accurate and complete information when creating an account.
              You are responsible for maintaining the confidentiality of your account credentials.
            </p>

            <h3>Account Security</h3>
            <p>
              You are responsible for all activities that occur under your account.
              You must notify us immediately of any unauthorized access or use.
            </p>

            <h2>4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal purpose</li>
              <li>Upload malicious code or attempt to compromise the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use the Service to harass, abuse, or harm others</li>
              <li>Violate intellectual property rights</li>
              <li>Share your account credentials with others</li>
              <li>Circumvent rate limits or access controls</li>
            </ul>

            <h2>5. Intellectual Property</h2>
            <h3>Your Content</h3>
            <p>
              You retain ownership of content you create or upload to the Service.
              By using the Service, you grant us a license to host, store, and process
              your content as necessary to provide the Service.
            </p>

            <h3>AI-Generated Content</h3>
            <p>
              Content generated by AI features is provided for your use. You are responsible
              for reviewing and validating AI-generated content before use.
            </p>

            <h2>6. AI Features</h2>
            <h3>No Guarantee of Accuracy</h3>
            <p>
              AI-generated content may contain errors or inaccuracies. You should review
              all AI output before relying on it.
            </p>

            <h3>Data Processing</h3>
            <p>
              Content you provide to AI features may be processed by third-party AI providers.
              See our Privacy Policy for details.
            </p>

            <h2>7. Service Availability</h2>
            <p>
              We do not guarantee uninterrupted access to the Service. We may modify, suspend,
              or discontinue the Service at any time. We will provide notice when possible.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul>
              <li>The Service is provided "AS IS" without warranties of any kind</li>
              <li>We are not liable for indirect, incidental, special, or consequential damages</li>
              <li>Our total liability is limited to the amount you paid for the Service in the past 12 months</li>
            </ul>

            <h2>9. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes
              by posting the new Terms and updating the effective date. Continued use of the Service
              after changes constitutes acceptance.
            </p>

            <h2>10. Contact</h2>
            <p>
              For questions about these Terms, contact us at:
              <a href="mailto:legal@audraflow.io">legal@audraflow.io</a>
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div>
                <span>Version 1.0</span>
                <span className="mx-2">•</span>
                <span>Effective: January 27, 2026</span>
              </div>
              <Link
                to="/privacy"
                className="text-blue-600 hover:text-blue-700"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
